import type { ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { warehouseTerms } from '@/features/warehouse/warehouseTerms'
import { getWarehouseDevice } from '@/shared/auth/warehouseDeviceStorage'
import { clearWarehouseSession } from '@/shared/auth/warehouseSessionStorage'
import { useClientHydrated } from '@/shared/hooks/useClientHydrated'

const nav = [
  { to: '/warehouse/home', label: warehouseTerms.modules.home, icon: 'home' },
  { to: '/warehouse/attendance', label: warehouseTerms.modules.attendance, icon: 'how_to_reg' },
  { to: '/warehouse/stock', label: warehouseTerms.modules.stock, icon: 'inventory_2' },
  { to: '/warehouse/dispatches', label: warehouseTerms.modules.dispatches, icon: 'local_shipping' },
] as const

export function WarehouseShell({ children }: { children: ReactNode }) {
  const hydrated = useClientHydrated()
  const device = hydrated ? getWarehouseDevice() : null
  const navigate = useNavigate()
  const { pathname } = useLocation()

  function handleLogout() {
    clearWarehouseSession()
    navigate('/warehouse/login', { replace: true })
  }

  return (
    <div className="min-h-screen flex flex-col max-w-5xl mx-auto">
      <header className="sticky top-0 z-10 bg-surface-container-lowest border-b border-outline-variant px-4 h-14 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="material-symbols-outlined text-primary shrink-0">warehouse</span>
          <div className="min-w-0">
            <p className="font-headline text-sm font-bold text-primary leading-tight">{warehouseTerms.appName}</p>
            <p className="text-caption text-on-surface-variant font-mono truncate max-w-[200px]">
              {device ? `${device.warehouseName} · ${device.deviceName}` : '—'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="text-xs text-on-surface-variant hover:text-primary whitespace-nowrap min-h-[44px]"
          aria-label={`${warehouseTerms.shell.logout} — akhiri sesi ${warehouseTerms.roles.headWarehouse.toLowerCase()}`}
        >
          {warehouseTerms.shell.logout}
        </button>
      </header>

      <main className="flex-1 p-4 pb-20">{children}</main>

      <nav className="fixed bottom-0 left-0 right-0 bg-surface-container-lowest border-t border-outline-variant safe-area-pb">
        <div className="max-w-5xl mx-auto flex justify-around px-1 py-1">
          {nav.map((item) => {
            const isActive = pathname === item.to || pathname.startsWith(`${item.to}/`)
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex flex-col items-center gap-0.5 px-2 py-2 rounded-lg text-caption min-w-[56px] min-h-[44px] justify-center transition-colors ${
                  isActive ? 'text-primary font-bold bg-primary-container/30' : 'text-on-surface-variant'
                }`}
              >
                <span className="material-symbols-outlined">{item.icon}</span>
                {item.label}
              </Link>
            )
          })}
        </div>
      </nav>
    </div>
  )
}
